import type {
  Charter,
  CharterAttachmentBody,
  CharterAttachmentPayload,
  CharterForTransactions,
  CharterLedger,
  CharterStatsPayload,
  CharterStatus,
  CharterSummary,
  ConfirmCharterAttachmentUploadRequest,
  CreateCharterRequest,
  PrepareCharterAttachmentUploadRequest,
  PrepareCharterAttachmentUploadResponse,
  SearchChartersRequest,
  UpdateCharterLedgerPayload,
  UpdateCharterRequest,
} from '@ankor-io/common/types/charter'
import type {
  CreateTransactionBody,
  ListTransactionsRequest,
  TransactionPayload,
} from '@ankor-io/common/types/transaction/transaction'

import { type HttpConfig, http } from '@/api/client/http'
import { createVesselConnectedAccountPaths } from '@/api/paths'

export const charterService = {
  // ========== GET Methods ==========

  /**
   * Search charters for an organization and connected account.
   */
  searchCharters(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    params?: SearchChartersRequest,
    config?: HttpConfig,
  ): Promise<CharterSummary[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const searchPath = paths.chartersSearch()

    const searchParams = new URLSearchParams()
    if (params?.query) {
      searchParams.set('query', params.query)
    }

    if (params?.status && params.status.length > 0) {
      searchParams.set('status', params.status.join(','))
    }

    if (params?.startDateFrom) {
      searchParams.set('startDateFrom', params.startDateFrom)
    }

    if (params?.startDateTo) {
      searchParams.set('startDateTo', params.startDateTo)
    }

    if (params?.endDateFrom) {
      searchParams.set('endDateFrom', params.endDateFrom)
    }

    if (params?.endDateTo) {
      searchParams.set('endDateTo', params.endDateTo)
    }

    if (params?.charterFeeMin !== undefined) {
      searchParams.set('charterFeeMin', String(params.charterFeeMin))
    }

    if (params?.charterFeeMax !== undefined) {
      searchParams.set('charterFeeMax', String(params.charterFeeMax))
    }

    if (params?.apaMin !== undefined) {
      searchParams.set('apaMin', String(params.apaMin))
    }

    if (params?.apaMax !== undefined) {
      searchParams.set('apaMax', String(params.apaMax))
    }

    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    if (params?.sortBy) {
      searchParams.set('sortBy', params.sortBy)
    }

    if (params?.sortOrder) {
      searchParams.set('sortOrder', params.sortOrder)
    }

    if (params?.currencyCode) {
      searchParams.set('currencyCode', params.currencyCode)
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${searchPath}?${queryString}` : searchPath

    return http.get<CharterSummary[]>(url, config)
  },

  /**
   * List charters for transactions for an organization and connected account.
   */
  listChartersForTransactions(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    status?: CharterStatus[],
    config?: HttpConfig,
  ): Promise<CharterForTransactions[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const urlPath = paths.chartersForTransactions()

    const searchParams = new URLSearchParams()
    if (status && status.length > 0) {
      searchParams.set('status', status.join(','))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${urlPath}?${queryString}` : urlPath

    return http.get<CharterForTransactions[]>(url, config)
  },

  /**
   * Get a charter by ID for an organization and connected account.
   */
  getCharterById(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    config?: HttpConfig,
  ): Promise<CharterSummary> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterById(charterId)

    return http.get<CharterSummary>(url, config)
  },

  /**
   * Get charter statistics for an organization and connected account.
   */
  getCharterStats(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    config?: HttpConfig,
  ): Promise<CharterStatsPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterStats(charterId)

    return http.get<CharterStatsPayload>(url, config)
  },

  /**
   * List transactions for a charter.
   */
  listCharterTransactions(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    params?: ListTransactionsRequest,
    config?: HttpConfig,
  ): Promise<TransactionPayload[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const transactionsPath = paths.charterTransactions(charterId)

    const searchParams = new URLSearchParams()
    if (params?.status && params.status.length > 0) {
      searchParams.set('status', params.status.join(','))
    }

    if (params?.type && params.type.length > 0) {
      searchParams.set('type', params.type.join(','))
    }

    if (params?.categoryId) {
      searchParams.set('categoryId', params.categoryId)
    }

    if (params?.vendor) {
      searchParams.set('vendor', params.vendor)
    }

    if (params?.dateFrom) {
      searchParams.set('dateFrom', params.dateFrom)
    }

    if (params?.dateTo) {
      searchParams.set('dateTo', params.dateTo)
    }

    if (params?.amountMinCents !== undefined) {
      searchParams.set('amountMinCents', String(params.amountMinCents))
    }

    if (params?.amountMaxCents !== undefined) {
      searchParams.set('amountMaxCents', String(params.amountMaxCents))
    }

    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    if (params?.sortBy) {
      searchParams.set('sortBy', params.sortBy)
    }

    if (params?.sortOrder) {
      searchParams.set('sortOrder', params.sortOrder)
    }

    if (params?.createdBy) {
      searchParams.set('createdBy', params.createdBy)
    }

    if (params?.paymentMethod && params.paymentMethod.length > 0) {
      searchParams.set('paymentMethod', params.paymentMethod.join(','))
    }

    if (params?.excludeFailed !== undefined) {
      searchParams.set('excludeFailed', String(params.excludeFailed))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${transactionsPath}?${queryString}` : transactionsPath

    return http.get<TransactionPayload[]>(url, config)
  },

  /**
   * Get charter ledger PDF for an organization and connected account.
   * Returns a Blob response for PDF download.
   */
  getCharterLedger(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    config?: HttpConfig,
  ): Promise<Blob> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterLedger(charterId)

    return http.get<Blob>(url, { ...config, responseType: 'blob' as const })
  },

  /**
   * Get charter signing-off ledger PDF for an organization and connected account.
   * Returns a Blob response for PDF download.
   */
  getCharterSigningOffLedger(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    config?: HttpConfig,
  ): Promise<Blob> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterSigningOffLedger(charterId)

    return http.get<Blob>(url, { ...config, responseType: 'blob' as const })
  },

  /**
   * Get charter digital sign-off ledger for an organization and connected account.
   */
  getCharterDigitalSignOffLedger(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    config?: HttpConfig,
  ): Promise<{ ledger: CharterLedger }> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterDigitalSignOffLedger(charterId)

    return http.get<{ ledger: CharterLedger }>(url, config)
  },

  /**
   * List charter attachments for an organization and connected account.
   */
  listCharterAttachments(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    params?: { take?: number; skip?: number },
    config?: HttpConfig,
  ): Promise<CharterAttachmentPayload[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const attachmentsPath = paths.charterAttachments(charterId)

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${attachmentsPath}?${queryString}` : attachmentsPath

    return http.get<{ attachments: CharterAttachmentPayload[] }>(url, config).then((res) => res.attachments)
  },

  /**
   * Get a charter attachment by ID for an organization and connected account.
   */
  getCharterAttachmentById(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    attachmentId: string,
    config?: HttpConfig,
  ): Promise<CharterAttachmentPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterAttachmentById(charterId, attachmentId)

    return http.get<{ attachment: CharterAttachmentPayload }>(url, config).then((res) => res.attachment)
  },

  // ========== POST Methods ==========

  /**
   * Create a new charter for an organization and connected account.
   */
  createCharter(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    payload: CreateCharterRequest,
    config?: HttpConfig,
  ): Promise<Charter> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charter()

    return http.post<Charter, CreateCharterRequest>(url, payload, config)
  },

  /**
   * Create a transaction for a charter.
   */
  createCharterTransaction(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    payload: CreateTransactionBody,
    config?: HttpConfig,
  ): Promise<TransactionPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterTransaction(charterId)

    return http.post<TransactionPayload, CreateTransactionBody>(url, payload, config)
  },

  /**
   * Create a charter attachment for an organization and connected account.
   */
  createCharterAttachment(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    payload: CharterAttachmentBody,
    config?: HttpConfig,
  ): Promise<CharterAttachmentPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterAttachments(charterId)

    return http
      .post<{ attachment: CharterAttachmentPayload }, CharterAttachmentBody>(url, payload, config)
      .then((res) => res.attachment)
  },

  /**
   * Prepare charter attachment upload for an organization and connected account.
   */
  prepareCharterAttachmentUpload(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    payload: PrepareCharterAttachmentUploadRequest,
    config?: HttpConfig,
  ): Promise<PrepareCharterAttachmentUploadResponse> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterAttachmentPrepare(charterId)

    return http.post<PrepareCharterAttachmentUploadResponse, PrepareCharterAttachmentUploadRequest>(
      url,
      payload,
      config,
    )
  },

  /**
   * Confirm charter attachment upload for an organization and connected account.
   */
  confirmCharterAttachmentUpload(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    payload: ConfirmCharterAttachmentUploadRequest,
    config?: HttpConfig,
  ): Promise<CharterAttachmentPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterAttachmentConfirm(charterId)

    return http.post<CharterAttachmentPayload, ConfirmCharterAttachmentUploadRequest>(url, payload, config)
  },

  // ========== PUT Methods ==========

  /**
   * Update a charter for an organization and connected account.
   */
  updateCharter(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    payload: UpdateCharterRequest,
    config?: HttpConfig,
  ): Promise<Charter> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterById(charterId)

    return http.put<Charter, UpdateCharterRequest>(url, payload, config)
  },

  /**
   * Update charter digital sign-off ledger for an organization and connected account.
   */
  updateCharterDigitalSignOffLedger(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    payload: UpdateCharterLedgerPayload,
    config?: HttpConfig,
  ): Promise<CharterLedger> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterDigitalSigningOffLedger(charterId)

    return http.put<CharterLedger, UpdateCharterLedgerPayload>(url, payload, config)
  },

  // ========== DELETE Methods ==========

  /**
   * Delete a charter attachment for an organization and connected account.
   */
  deleteCharterAttachment(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    charterId: string,
    attachmentId: string,
    config?: HttpConfig,
  ): Promise<void> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.charterAttachmentById(charterId, attachmentId)

    return http.delete<{ success: boolean }>(url, config).then(() => undefined)
  },
}
